import React, { useState, useEffect } from 'react';
import { PurchaseOrder } from '../types';
import { purchaseOrderService } from '../services/purchaseOrderService';
import { 
  Plus, 
  Search, 
  ChevronLeft, 
  ChevronRight,
  Eye,
  Pencil,
  Trash2,
  Package,
  DollarSign,
  AlertCircle,
  XCircle,
  Filter
} from 'lucide-react';
import { customToast } from '../utils/customToast';
import PurchaseOrderModal from '../components/PurchaseOrder/PurchaseOrderModal';
import PurchaseOrderDetailsModal from '../components/PurchaseOrder/PurchaseOrderDetailsModal';
import ReceiveItemsModal from '../components/PurchaseOrder/ReceiveItemsModal';
import AddPaymentModal from '../components/PurchaseOrder/AddPaymentModal';
import { useTitle } from '../hooks/useTitle';
import { useTheme } from '../contexts/ThemeContext';
import { useCurrency } from '../hooks/useCurrency';


const PurchaseOrdersPage: React.FC = () => {
  useTitle('Purchase Orders');
  const { theme } = useTheme();
  const { formatCurrency } = useCurrency();
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isReceiveModalOpen, setIsReceiveModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedPO, setSelectedPO] = useState<PurchaseOrder | null>(null);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterPaymentStatus, setFilterPaymentStatus] = useState<string>('');
  const [stats, setStats] = useState<any>(null);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadPurchaseOrders();
    loadStatistics();
  }, []);

  const loadPurchaseOrders = async () => {
    try {
      setLoading(true);
      const data = await purchaseOrderService.getPurchaseOrders();
      setPurchaseOrders(data);
    } catch (error) {
      console.error('Error loading purchase orders:', error);
      customToast.error('Failed to load purchase orders');
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const data = await purchaseOrderService.getStatistics();
      setStats(data);
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  const handleAddPO = () => {
    setSelectedPO(null);
    setModalMode('create');
    setIsModalOpen(true);
  };

  const handleEditPO = (po: PurchaseOrder) => {
    setSelectedPO(po);
    setModalMode('edit');
    setIsModalOpen(true);
  };

  const handleViewPO = (po: PurchaseOrder) => {
    setSelectedPO(po);
    setIsDetailsModalOpen(true);
  };

  const handleReceiveItems = (po: PurchaseOrder) => {
    setSelectedPO(po);
    setIsReceiveModalOpen(true);
  };

  const handleAddPayment = (po: PurchaseOrder) => {
    setSelectedPO(po);
    setIsPaymentModalOpen(true);
  };

  const handleDeletePO = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this purchase order?')) {
      try {
        await purchaseOrderService.deletePurchaseOrder(id);
        customToast.success('Purchase order deleted successfully!');
        loadPurchaseOrders();
        loadStatistics();
      } catch (error: any) {
        console.error('Error deleting purchase order:', error);
        customToast.error(error.response?.data?.message || 'Failed to delete purchase order. Please try again.');
      }
    }
  };

  const handleCancelPO = async (id: number) => {
    if (window.confirm('Are you sure you want to cancel this purchase order?')) {
      try {
        await purchaseOrderService.cancelPurchaseOrder(id);
        customToast.success('Purchase order cancelled successfully!');
        loadPurchaseOrders();
        loadStatistics();
      } catch (error: any) {
        console.error('Error cancelling purchase order:', error);
        customToast.error(error.response?.data?.message || 'Failed to cancel purchase order. Please try again.');
      }
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedPO(null);
  };

  const handleDetailsModalClose = () => {
    setIsDetailsModalOpen(false);
    setSelectedPO(null);
  };

  const handleModalSuccess = () => {
    loadPurchaseOrders();
    loadStatistics();
    handleModalClose();
  };

  const handleDetailsSuccess = () => {
    loadPurchaseOrders();
    loadStatistics();
  };

  const filteredPOs = (purchaseOrders || []).filter(po => {
    const matchesSearch = 
      po.po_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      po.supplier?.name.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = !filterStatus || po.status === filterStatus;
    
    const paymentStatus = po.paid_amount === 0 ? 'unpaid' : 
                         po.paid_amount >= po.total_amount ? 'paid' : 'partial';
    const matchesPaymentStatus = !filterPaymentStatus || paymentStatus === filterPaymentStatus;

    return matchesSearch && matchesStatus && matchesPaymentStatus;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentPOs = filteredPOs.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredPOs.length / itemsPerPage);

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      received: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      partial: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
      completed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      cancelled: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${colors[status] || colors.pending}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getPaymentStatusBadge = (po: PurchaseOrder) => {
    const paidAmount = Number(po.paid_amount || 0);
    const totalAmount = Number(po.total_amount || 0);
    
    if (paidAmount === 0 || totalAmount === 0) {
      return <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">Unpaid</span>;
    } else if (paidAmount >= totalAmount) {
      return <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">Paid</span>;
    } else {
      return <span className="px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">Partial</span>;
    }
  };

  return (
    <div className="space-y-3 sm:space-y-4 lg:space-y-6">
      {/* Header */}
      <div className="flex flex-col space-y-3 sm:space-y-4">
        <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className={`text-xl sm:text-2xl lg:text-3xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              Purchase Orders
            </h1>
            <p className={`mt-1 text-sm ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Manage purchase orders and supplier invoices.
            </p>
          </div>
          
          <div className="flex gap-3 flex-col sm:flex-row">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center justify-center space-x-2 px-3 py-2 sm:px-4 rounded-xl border transition-all duration-200 shadow-sm hover:shadow-md touch-manipulation ${
                theme === 'dark'
                  ? 'border-gray-600 text-gray-300 bg-gray-800 hover:bg-gray-700 hover:border-gray-500'
                  : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400'
              }`}
              title={showFilters ? 'Hide Filters' : 'Show Filters'}
            >
              <Filter size={16} />
              <span className="text-sm">Filters</span>
            </button>
            
            <button
              onClick={handleAddPO}
              className="flex items-center justify-center space-x-2 px-3 py-2 sm:px-4 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-all duration-200 shadow-lg hover:shadow-xl font-medium touch-manipulation"
              title="Add New Purchase Order"
            >
              <Plus size={16} />
              <span className="text-sm">Create Purchase Order</span>
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <Search size={16} className={`absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
          }`} />
          <input
            type="text"
            placeholder="Search purchase orders..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg sm:rounded-xl border transition-all duration-200 shadow-sm focus:shadow-md touch-manipulation ${
              theme === 'dark'
                ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500'
                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:bg-gray-50'
            }`}
          />
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className={`p-3 sm:p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className={`text-xs sm:text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Total POs</p>
              {loading ? (
                <div className={`mt-1 sm:mt-2 h-6 sm:h-8 w-20 rounded-lg animate-pulse ${
                  theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                }`} />
              ) : (
                <p className={`text-lg sm:text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{(stats?.total_purchase_orders || 0).toLocaleString()}</p>
              )}
            </div>
            <div className="p-2 sm:p-3 bg-blue-100 rounded-lg flex-shrink-0">
              <Package className="w-4 h-4 sm:w-6 sm:h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className={`p-3 sm:p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className={`text-xs sm:text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Total Amount</p>
              {loading ? (
                <div className={`mt-1 sm:mt-2 h-6 sm:h-8 w-24 rounded-lg animate-pulse ${
                  theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                }`} />
              ) : (
                <p className={`text-base sm:text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(stats?.total_amount || 0)}</p>
              )}
            </div>
            <div className="p-2 sm:p-3 bg-purple-100 rounded-lg flex-shrink-0">
              <DollarSign className="w-4 h-4 sm:w-6 sm:h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className={`p-3 sm:p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className={`text-xs sm:text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Pending</p>
              {loading ? (
                <div className={`mt-1 sm:mt-2 h-6 sm:h-8 w-20 rounded-lg animate-pulse ${
                  theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                }`} />
              ) : (
                <p className={`text-lg sm:text-2xl font-bold text-yellow-600`}>{(stats?.pending_orders || 0).toLocaleString()}</p>
              )}
            </div>
            <div className="p-2 sm:p-3 bg-yellow-100 rounded-lg flex-shrink-0">
              <AlertCircle className="w-4 h-4 sm:w-6 sm:h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className={`p-3 sm:p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className={`text-xs sm:text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Due Amount</p>
              {loading ? (
                <div className={`mt-1 sm:mt-2 h-6 sm:h-8 w-24 rounded-lg animate-pulse ${
                  theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                }`} />
              ) : (
                <p className={`text-base sm:text-xl font-bold text-red-600`}>{formatCurrency(stats?.due_amount || 0)}</p>
              )}
            </div>
            <div className="p-2 sm:p-3 bg-red-100 rounded-lg flex-shrink-0">
              <XCircle className="w-4 h-4 sm:w-6 sm:h-6 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className={`p-4 rounded-lg border ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                Status
              </label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className={`w-full px-4 py-2 rounded-lg border ${
                  theme === 'dark' 
                    ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                    : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                }`}
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="partial">Partial</option>
                <option value="received">Received</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                Payment Status
              </label>
              <select
                value={filterPaymentStatus}
                onChange={(e) => setFilterPaymentStatus(e.target.value)}
                className={`w-full px-4 py-2 rounded-lg border ${
                  theme === 'dark' 
                    ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                    : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                }`}
              >
                <option value="">All Payment Status</option>
                <option value="paid">Paid</option>
                <option value="partial">Partial</option>
                <option value="unpaid">Unpaid</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Content Area */}
      {loading ? (
        <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg overflow-hidden`}>
          <div className="space-y-4 p-4 sm:p-6">
            {[...Array(5)].map((_, idx) => (
              <div key={idx} className={`h-16 rounded-lg animate-pulse ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`} />
            ))}
          </div>
        </div>
      ) : currentPOs.length === 0 ? (
        <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg overflow-hidden`}>
          <div className="text-center py-12">
            <Package className={`mx-auto h-12 w-12 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`} />
            <h3 className={`mt-2 text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-900'}`}>
              No purchase orders found
            </h3>
            <p className={`mt-1 text-sm ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
              {searchTerm || filterStatus || filterPaymentStatus ? 'Try adjusting your filters' : 'Use the Create PO button above to get started'}
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg overflow-hidden`}>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className={theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}>
                  <tr>
                    <th className={`px-4 sm:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                    }`}>PO Number</th>
                    <th className={`px-4 sm:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                    }`}>Supplier</th>
                    <th className={`px-4 sm:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                    }`}>Amount</th>
                    <th className={`px-4 sm:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                    }`}>Status</th>
                    <th className={`px-4 sm:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                    }`}>Payment</th>
                    <th className={`px-4 sm:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                    }`}>Actions</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}>
                  {currentPOs.map((po) => (
                    <tr 
                      key={po.id}
                      className={`${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} transition-colors`}
                    >
                      <td className={`px-4 sm:px-6 py-4 whitespace-nowrap font-medium ${
                        theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                      }`}>
                        {po.po_number}
                      </td>
                      <td className={`px-4 sm:px-6 py-4 whitespace-nowrap text-sm ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-900'
                      }`}>
                        {po.supplier?.name}
                      </td>
                      <td className={`px-4 sm:px-6 py-4 whitespace-nowrap text-sm ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-900'
                      }`}>
                        <div className="font-medium">{formatCurrency(po.total_amount)}</div>
                        <div className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                          Due: {formatCurrency(po.due_amount)}
                        </div>
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(po.status)}
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                        {getPaymentStatusBadge(po)}
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2 flex-wrap">
                          <button
                            onClick={() => handleViewPO(po)}
                            className={`p-1.5 rounded-lg transition-all ${
                              theme === 'dark' 
                                ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-600' 
                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                            }`}
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          
                          {(po.status === 'pending' || po.status === 'partial') && (
                            <button
                              onClick={() => handleReceiveItems(po)}
                              className={`px-2 py-1 text-xs rounded-lg transition-all flex items-center gap-1 ${
                                theme === 'dark' 
                                  ? 'bg-green-900/30 text-green-300 hover:bg-green-900/50' 
                                  : 'bg-green-100 text-green-700 hover:bg-green-200'
                              }`}
                              title="Receive Items"
                            >
                              <Package className="w-3 h-3" />
                              Receive
                            </button>
                          )}
                          
                          {(po.status === 'received' || po.status === 'partial' || po.status === 'completed') && Number(po.due_amount) > 0 && (
                            <button
                              onClick={() => handleAddPayment(po)}
                              className={`px-2 py-1 text-xs rounded-lg transition-all flex items-center gap-1 ${
                                theme === 'dark' 
                                  ? 'bg-blue-900/30 text-blue-300 hover:bg-blue-900/50' 
                                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                              }`}
                              title="Add Payment"
                            >
                              <DollarSign className="w-3 h-3" />
                              Payment
                            </button>
                          )}
                          
                          {po.status === 'pending' && (
                            <button
                              onClick={() => handleEditPO(po)}
                              className={`p-1.5 rounded-lg transition-all ${
                                theme === 'dark' 
                                  ? 'text-blue-400 hover:text-blue-300 hover:bg-gray-600' 
                                  : 'text-blue-600 hover:text-blue-700 hover:bg-gray-100'
                              }`}
                              title="Edit"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                          )}
                          
                          {(po.status === 'pending' || po.status === 'cancelled') && po.paid_amount === 0 && (
                            <button
                              onClick={() => handleDeletePO(po.id)}
                              className={`p-1.5 rounded-lg transition-all ${
                                theme === 'dark' 
                                  ? 'text-red-400 hover:text-red-300 hover:bg-gray-600' 
                                  : 'text-red-600 hover:text-red-700 hover:bg-gray-100'
                              }`}
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                          
                          {(po.status === 'pending' || po.status === 'partial') && (
                            <button
                              onClick={() => handleCancelPO(po.id)}
                              className={`px-2 py-1 text-xs rounded-lg transition-all ${
                                theme === 'dark' 
                                  ? 'bg-red-900/30 text-red-300 hover:bg-red-900/50' 
                                  : 'bg-red-100 text-red-600 hover:bg-red-200'
                              }`}
                              title="Cancel"
                            >
                              Cancel
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className={`px-4 sm:px-6 py-4 border-t ${theme === 'dark' ? 'border-gray-700 bg-gray-750' : 'border-gray-200 bg-gray-50'}`}>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className={`text-xs sm:text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-700'}`}>
                    Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredPOs.length)} of {filteredPOs.length} purchase orders
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1 transition-all ${
                        currentPage === 1
                          ? theme === 'dark' ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : theme === 'dark' ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <ChevronLeft className="w-4 h-4" />
                      <span>Previous</span>
                    </button>
                    <div className={`flex items-center px-3 py-1.5 rounded-lg text-sm font-medium ${
                      theme === 'dark' ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900'
                    }`}>
                      Page {currentPage} of {totalPages}
                    </div>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1 transition-all ${
                        currentPage === totalPages
                          ? theme === 'dark' ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : theme === 'dark' ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <span>Next</span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Modals */}
      {isModalOpen && (
        <PurchaseOrderModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
          purchaseOrder={selectedPO}
          mode={modalMode}
        />
      )}

      {isDetailsModalOpen && selectedPO && (
        <PurchaseOrderDetailsModal
          isOpen={isDetailsModalOpen}
          onClose={handleDetailsModalClose}
          purchaseOrder={selectedPO}
          onSuccess={handleDetailsSuccess}
        />
      )}

      {isReceiveModalOpen && selectedPO && (
        <ReceiveItemsModal
          isOpen={isReceiveModalOpen}
          onClose={() => setIsReceiveModalOpen(false)}
          purchaseOrder={selectedPO}
          onSuccess={() => {
            setIsReceiveModalOpen(false);
            loadPurchaseOrders();
            loadStatistics();
          }}
        />
      )}

      {isPaymentModalOpen && selectedPO && (
        <AddPaymentModal
          isOpen={isPaymentModalOpen}
          onClose={() => setIsPaymentModalOpen(false)}
          purchaseOrder={selectedPO}
          onSuccess={() => {
            setIsPaymentModalOpen(false);
            loadPurchaseOrders();
            loadStatistics();
          }}
        />
      )}
    </div>
  );
};

export default PurchaseOrdersPage;
